import { Injectable } from '@angular/core';
import { createStore, select, withProps } from '@ngneat/elf';
import { localStorageStrategy, persistState } from '@ngneat/elf-persist-state';
import { Observable } from 'rxjs';
import { tenantStorageKey } from '../shared/helpers/tenant';

const MESSAGE_READ_STATE_STORE_NAME = 'andorMessageReadState.v1';

export interface MessageReadState {
  // Per-room watermark: messages with `time.getTime() > lastReadAt[roomId]` are unread.
  // Stored as ms-since-epoch so we avoid Date rehydration coercion and keep comparisons fast.
  lastReadAt: Record<string, number>;
}

const messageReadStateStore = createStore(
  { name: MESSAGE_READ_STATE_STORE_NAME },
  withProps<MessageReadState>({ lastReadAt: {} })
);

persistState(messageReadStateStore, {
  key: tenantStorageKey(MESSAGE_READ_STATE_STORE_NAME),
  storage: localStorageStrategy,
});

@Injectable({
  providedIn: 'root',
})
export class MessageReadStateStore {
  private _store = messageReadStateStore;
  lastReadAt$ = this._store.pipe(select((s) => s.lastReadAt));

  get value(): Readonly<Record<string, number>> {
    return this._store.getValue().lastReadAt;
  }

  lastReadAtFor$(roomId: string): Observable<number> {
    return this._store.pipe(select((s) => s.lastReadAt[roomId] ?? 0));
  }

  // Moves the watermark forward only. No-op when `timeMs` is not ahead of the
  // current value, so callers can safely call on every frame / scroll event.
  markReadUpTo(roomId: string, timeMs: number): void {
    this._store.update((state) => {
      const current = state.lastReadAt[roomId] ?? 0;
      if (timeMs <= current) return state;
      return { ...state, lastReadAt: { ...state.lastReadAt, [roomId]: timeMs } };
    });
  }

  // Marks the given rooms as read at the current time. Caller supplies the
  // room ids explicitly because the read-state store has no way to know
  // which rooms exist — its `lastReadAt` map only contains rooms it has
  // already been written to, so right after `markAllAsUnread()` cleared
  // that map an empty self-enumeration would be a silent no-op. Pass
  // `messageStore.knownRoomIds()` when the intent is "mark everything as
  // read".
  markAllAsRead(roomIds: Iterable<string>): void {
    const now = Date.now();
    this._store.update((state) => {
      const next: Record<string, number> = { ...state.lastReadAt };
      let changed = false;
      for (const id of roomIds) {
        next[id] = now;
        changed = true;
      }
      if (!changed) return state;
      return { ...state, lastReadAt: next };
    });
  }

  // Testing helper: clears every watermark so all remote/system messages in
  // the store become unread again. Only the in-memory & persisted read-state
  // is affected; the messages themselves are untouched.
  markAllAsUnread(): void {
    this._store.update((state) => ({ ...state, lastReadAt: {} }));
  }

  // Sets the watermark only if none exists yet. Used on first run so that
  // already-persisted history isn't treated as unread on the next load.
  seedIfMissing(roomId: string, timeMs: number): void {
    this._store.update((state) => {
      if (state.lastReadAt[roomId] !== undefined) return state;
      return { ...state, lastReadAt: { ...state.lastReadAt, [roomId]: timeMs } };
    });
  }
}
